import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Empty,
  GameLibrary,
  Link,
  Logo,
  Page,
  ui,
  type OpenCaseDetail,
  type ShelfCase,
} from '../ui';
import {
  GAMES,
  ROOM_BACKDROP,
  galleryItems,
  gameBySlug,
  playModes,
  statusLabel,
  type Game,
} from '../games';
import { orderedStoreLinks, storePlatformShort } from '../store-links';
import { LIBRARY_PILE_KEY, pileFrom, usePileOrder } from '../pile-order';

/**
 * The room, and whichever case is out of the pile.
 *
 * **This is the whole site.** `/` is the pile shut and `/g/<slug>` is the same
 * room with one case open, which is why both routes render this component:
 * opening a case is a navigation, so a case in somebody's hands has an address
 * and the back button puts it down.
 *
 * There is no page behind the case. Everything a game has to say — what it is,
 * how you play it, what it looks like, where to buy it — is printed on the
 * insert and the disc, because a case that opens onto a link to the real page
 * is a menu item rather than an object.
 */

function coverFor(game: Game): string | null {
  return game.art.cover;
}

/**
 * What the disc is printed with.
 *
 * The game's own label if it has one, and its cover if it does not — which is
 * every title imported from Steam, since Steam ships no square asset. A disc
 * printed with the key art is what a disc looks like; `useDiscLabel` crops it
 * square and prints the platform mark at twelve o'clock.
 */
function discFor(game: Game): string | null {
  return game.art.disc ?? game.art.cover;
}

/**
 * Whose logo goes on the spine band and the disc.
 *
 * One shop gets its own mark; several get the machine instead. A case spine
 * carries the thing you are choosing between, and for a game sold in one place
 * that is the shop. For a game on Steam *and* Epic the shop is not a
 * distinguishing fact — printing one would state a preference we do not have —
 * and what stays true of every copy is that it runs on a PC.
 */
function markFor(game: Game): string | null {
  const shops = orderedStoreLinks([...game.links]);

  const only = shops.length === 1 ? shops[0] : undefined;
  if (only) return only.platform;

  const os: Record<string, string> = { Windows: 'windows', macOS: 'macos', Linux: 'linux' };
  for (const platform of game.platforms) {
    const mark = os[platform];
    if (mark) return mark;
  }

  return shops[0]?.platform ?? null;
}

function detailFor(game: Game): OpenCaseDetail {
  return {
    tagline: game.tagline,
    status: statusLabel(game),
    releaseLine: game.releaseLine,
    modes: playModes(game),
    lede: game.description[0] ?? '',
    /*
      Anything the leaflet cannot show.

      More paragraphs, any features, or a first paragraph long enough that the
      clamp will bite. The length is the one soft number here and it is well
      under what five lines of the leaflet hold, so the button never promises
      more when there is none.
    */
    hasMore:
      game.description.length > 1 ||
      game.features.length > 0 ||
      (game.description[0]?.length ?? 0) > 240,
    description: game.description,
    features: game.features,
    shots: galleryItems(game)
      .filter((item) => item.kind === 'screenshot')
      .map((item) => item.url),
    /*
      The label is decided here rather than in the scene.

      `case-scene.tsx` is presentational on purpose — it takes cases and calls
      back — and a component that reached into `store-links` for a name would be
      a component that knows what a shop is. It draws what it is handed.
    */
    shops: orderedStoreLinks([...game.links]).map((link) => ({
      platform: link.platform,
      label: storePlatformShort(link.platform),
      url: link.url,
    })),
  };
}

export function HomeRoute(): React.ReactElement {
  const { slug } = useParams({ strict: false }) as { slug?: string };
  const navigate = useNavigate();

  const open = slug ? gameBySlug(slug) : null;

  const { order, putOnTop } = usePileOrder(LIBRARY_PILE_KEY);

  /*
    Opening and shutting are navigations, so the pile's own memory is updated
    on the way past — the case somebody just had is the one on top next time,
    whether they got here by pressing it or by following a link.
  */
  const take = useCallback(
    (next: string) => {
      putOnTop(open?.slug ?? null);
      void navigate({ to: '/g/$slug', params: { slug: next } });
    },
    [navigate, open, putOnTop],
  );

  const shut = useCallback(() => {
    putOnTop(open?.slug ?? null);
    void navigate({ to: '/' });
  }, [navigate, open, putOnTop]);

  const cases: ShelfCase[] = useMemo(
    () =>
      pileFrom(
        GAMES.map((game) => ({
          slug: game.slug,
          name: game.name,
          coverUrl: coverFor(game),
          discUrl: discFor(game),
          mark: markFor(game),
          gone: false,
        })),
        (item) => item.slug,
        order,
      ),
    [order],
  );

  /*
    The name in the header band, and the strip is deliberately not on it.

    The lockup is the mark and the name together everywhere else. Here the name
    stands alone: the room is already full of objects, and a five-bar chart over
    a pile of game cases reads as another object in the scene rather than as the
    title of the page. `Logo` still sets it, so the letters are the wordmark
    rather than a `<span>` with the tracking guessed at.

    Smaller than it was, because it is inside a band now rather than floating on
    the ground. The band gives it the presence the size used to have to carry on
    its own.
  */
  const title = <Logo display bars={false} size="clamp(1.75rem, 6vw, 4rem)" href={null} onDark />;

  const smallPrint = <Link href="/legal">Legal</Link>;

  if (cases.length === 0) {
    return (
      <Page surface>
        <div
          className={ui.pageFullCentre ?? ''}
          style={{ flexDirection: 'column', gap: 'var(--gb-space-8)' }}
        >
          {title}
          <Empty icon="gamepad">No games yet. They turn up here as we finish them.</Empty>
          {smallPrint}
        </div>
      </Page>
    );
  }

  return (
    <Page surface>
      <GameLibrary
        cases={cases}
        openSlug={open?.slug ?? null}
        detail={open === null ? null : detailFor(open)}
        wallUrl={ROOM_BACKDROP}
        title={title}
        smallPrint={smallPrint}
        onOpen={take}
        onShut={shut}
      />
    </Page>
  );
}
